import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography
} from "@material-ui/core";
import copy from "copy-to-clipboard";
import { Link } from "gatsby";
import { useSnackbar } from "material-ui-snackbar-provider";
import React, { useEffect } from "react";
import AnimatedOilCovers from "../../../../../content-modules/oil-covers/AnimatedOilCovers";
import useTranslate from "../../../../../hooks/use-translate";
import useUserReferralCode from "../../../../../hooks/use-user-referral-code";
import { analyticsTrackEvent } from "../../../../../services/analytics";
import { CustomCopyButton } from "../../../../user/containers/MyRewards/components/ReferralSection";
import ShareIcon from "../../../../user/containers/MyRewards/components/ReferralSection/icons/share";
import ShareModal from "../../../../user/containers/MyRewards/components/ReferralSection/ShareModal";
import { getReferralShareData } from "../../../../user/containers/MyRewards/components/ReferralSection/util";
import ActivationLayout from "../../../components/Layout";
import { useConfig } from "../../../hooks/useConfig";
import useRequireAuthenticated from "../../../hooks/useRequireAuthenticated";
import useRequireUnlockAllowed from "../../../hooks/useRequireUnlockAllowed";
import { RouteComponentProps } from "@reach/router";
import { useShareBoard } from "../../../../user/containers/MyRewards/components/ReferralSection/use-share-board";
import useXSmallHeightMediaQuery from "../../../../../hooks/use-xsmall-height-mq";

type Props = RouteComponentProps<{ podKeyOrSerialNumber: string }>;

export const CustomShareButton = styled(Button)(({ theme }) => ({
  "& span > svg": {
    position: `absolute`,
    top: theme.spacing(0.75),
    left: theme.spacing(3)
  }
}));

export const CustomTips = styled(Typography)(() => ({
  "& span": {
    display: "inline-block"
  }
}));

export default function ActivationStep5ReferralInstructions(props: Props) {
  useRequireAuthenticated();
  const isUnlockAllowed = useRequireUnlockAllowed();
  const t = useTranslate();
  const config = useConfig();
  const isXSmallHeight = useXSmallHeightMediaQuery();

  const snackbar = useSnackbar();
  const { data: referralCode } = useUserReferralCode();
  const shareData = getReferralShareData(referralCode);
  const {
    openShareBoard,
    isCustomShareModalOpen,
    handelCustomShareModalClose
  } = useShareBoard(shareData);

  const handleCopy = () => {
    copy(shareData.url);
    snackbar.showMessage(t("user.profile.section.referral.copysuccess"));
  };

  const handleShare = async () => {
    openShareBoard();
  };

  return (
    <ActivationLayout showLoading={!isUnlockAllowed} fullHeight>
      <Box>
        <Typography variant={isXSmallHeight ? "h5" : "h4"}>
          {t("activation.unlock.referral.title")}
        </Typography>
        <Box
          ml={{ xs: -2, sm: 0 }}
          mr={{ xs: -2, sm: 0 }}
          pt={{ xs: 3, md: 4 }}
          pb={{ xs: 1.8, md: 4 }}
        >
          <AnimatedOilCovers
            speed={1}
            direction="right"
            covers={config?.referralStepOilCovers || []}
            size="small"
          />
        </Box>
        <Typography variant={isXSmallHeight ? "body2" : "h6"}>
          {t("activation.unlock.referral.description")}
        </Typography>
        <Box pt={1.5} pb={2.25}>
          <CustomTips
            variant="body2"
            color="textSecondary"
            dangerouslySetInnerHTML={{
              __html: t("activation.unlock.referral.tips")
            }}
          />
        </Box>
        <Box
          bgcolor="grey.300"
          borderRadius="3px"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pt={1.5}
          pb={1.5}
          pl={2.5}
          pr={2.5}
          mt={{ xs: 0, md: 4 }}
        >
          <Typography variant="body2" noWrap className="hide-in-percy">
            {shareData.prettyLink}
          </Typography>
          <CustomCopyButton
            onClick={handleCopy}
            disabled={referralCode === ""}
            style={{
              backgroundColor: `white`
            }}
          >
            {t("activation.unlock.referral.button.copy")}
          </CustomCopyButton>
        </Box>
      </Box>

      <Box mt={isXSmallHeight ? 1.5 : 3}>
        <CustomShareButton
          variant="contained"
          color="primary"
          fullWidth
          disabled={referralCode === ""}
          startIcon={<ShareIcon />}
          onClick={handleShare}
        >
          {referralCode === "" ? (
            <CircularProgress size={16} color="secondary" />
          ) : (
            t("activation.unlock.referral.button.share")
          )}
        </CustomShareButton>
        <Box marginTop={1.5}>
          <Button
            variant="contained"
            color="default"
            fullWidth
            component={Link}
            to={"/us/en/shop"}
          >
            {t("activation.unlock.referral.button.shop")}
          </Button>
        </Box>
      </Box>
      <ShareModal
        shareData={shareData}
        open={isCustomShareModalOpen}
        copy={handleCopy}
        close={handelCustomShareModalClose}
      />
    </ActivationLayout>
  );
}
